import { CheckCircle } from "@mui/icons-material";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { eventClick, RefreshData } from "components/helper";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import DeleteModal from "components/Modals/DeleteModal";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { FaEye, FaPlusCircle, FaUsers } from "react-icons/fa";
import { IoCloseCircle } from "react-icons/io5";
import { MdDeleteForever, MdRestore } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { canManagePlatformPermissionGrants, getUserType } from "utils/permissionUtils";
import { formatUserOrganizations } from "utils/userMembershipUtils";

// Deactivated accounts live behind users/disabled; permanent deletion is offered from there,
// superadmins only, with a preview of what goes with the account.
const fetchDeactivatedUsers = () =>
  axios.get(`${API_URL}/users/disabled`).then((res) => res?.data?.data ?? []);

const fetchUserDeletePreview = (userId) =>
  axios.get(`${API_URL}/users/${userId}/delete-preview`).then((res) => res?.data?.data ?? null);

const Users = () => {
  const navigate = useNavigate();
  const [{ usersList, token, isUsersListLoading }, { refetchUsers }] = useKothar();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [confirmResendUser, setConfirmResendUser] = useState(null);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmReactivateUser, setConfirmReactivateUser] = useState(null);

  const currentUserType = getUserType();
  const isAdminOrSuperAdmin =
    currentUserType === "SUPER_ADMIN" ||
    currentUserType === "ADMIN" ||
    canManagePlatformPermissionGrants();
  const isSuperAdmin = currentUserType === "SUPER_ADMIN";

  useEffect(() => {
    if (!usersList?.length) {
      refetchUsers();
    }
  }, []);

  const {
    data: deactivatedUsers = [],
    isFetching: isDeactivatedLoading,
    refetch: refetchDeactivatedUsers,
  } = useQuery(["users-disabled"], fetchDeactivatedUsers, {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: showDeactivated,
  });

  const visibleUsers = showDeactivated ? deactivatedUsers : usersList;
  const isVisibleLoading = showDeactivated ? isDeactivatedLoading : isUsersListLoading;

  const askPermanentDelete = async (item) => {
    setPermanentDeleteTarget(item);
    setDeletePreview(null);
    setLoadingPreview(true);
    try {
      setDeletePreview(await fetchUserDeletePreview(item?.id));
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Could not load delete preview"
      );
      setPermanentDeleteTarget(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const permanentlyDeleteUser = () => {
    deleteData(
      `${API_URL}/users/${permanentDeleteTarget?.id}/permanent`,
      () => {
        setPermanentDeleteTarget(null);
        setDeletePreview(null);
        refetchDeactivatedUsers();
        refetchUsers();
      },
      token
    );
  };

  const reactivateUser = async () => {
    try {
      await axios.post(`${API_URL}/users/reactivate/${confirmReactivateUser?.id}`);
      toast.success("User reactivated");
      setConfirmReactivateUser(null);
      refetchDeactivatedUsers();
      refetchUsers();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Could not reactivate user"
      );
    }
  };

  const { isLoading: loadingResend, mutate: resendVerification } = useMutation(
    async (userId) => axios.post(`${API_URL}/users/${userId}/resend-invite`),
    {
      onSuccess() {
        toast.success("Invitation email resent. Ask the user to check their email.");
        refetchUsers();
      },
      onError() {
        toast.error("Failed to resend invitation email");
      },
    }
  );

  const wasResending = useRef(false);
  useEffect(() => {
    if (wasResending.current && !loadingResend) {
      setConfirmResendUser(null);
    }
    wasResending.current = loadingResend;
  }, [loadingResend]);

  const deleteUser = () => {
    deleteData(
      `${API_URL}/users/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchUsers();
      },
      token
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 180,
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={renderedCellValue || ""}>
            <span className="font-semibold text-app-text-secondary">
              {renderedCellValue || "-"}
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "username",
        header: "Username",
        size: 150,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
      },
      {
        accessorKey: "mobileNumber",
        header: "Contact",
        size: 140,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "type",
        header: "Role",
        size: 120,
      },
      {
        id: "organisation",
        header: "Organisation",
        size: 180,
        accessorFn: (row) => formatUserOrganizations(row),
      },
      {
        accessorKey: "emailVerified",
        header: "Email Verified",
        size: 130,
        Cell: ({ row }) =>
          row.original?.emailVerified === "Y" ? (
            <CheckCircle className="text-green-500 text-2xl" />
          ) : (
            <IoCloseCircle className="text-red-500 text-2xl" />
          ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 180,
        enableSorting: false,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              {item?.emailVerified !== "Y" && isSuperAdmin && (
                <Tooltip title="Resend Invitation Email" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setConfirmResendUser(item)}
                    disabled={loadingResend && confirmResendUser?.id === item?.id}
                  >
                    {loadingResend && confirmResendUser?.id === item?.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FaUsers className="text-primary" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View User" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/admin/user/view/${item?.id}`, {
                      state: { item },
                    })
                  }
                >
                  <FaEye className="text-primary" />
                </IconButton>
              </Tooltip>
              {!showDeactivated && (
                <Tooltip title="Edit User" arrow>
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigate(`/admin/user/edit/${item?.id}`, {
                        state: { item },
                      })
                    }
                  >
                    <AiFillEdit className="text-primary" />
                  </IconButton>
                </Tooltip>
              )}
              {!showDeactivated && (
                <Tooltip title="Delete User" arrow>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setOpenConfirmationModal({
                        state: true,
                        id: item?.id,
                      })
                    }
                  >
                    <AiFillDelete className="text-red-600" />
                  </IconButton>
                </Tooltip>
              )}
              {showDeactivated && (
                <Tooltip title="Reactivate User" arrow>
                  <IconButton size="small" onClick={() => setConfirmReactivateUser(item)}>
                    <MdRestore className="text-primary" />
                  </IconButton>
                </Tooltip>
              )}
              {showDeactivated && isSuperAdmin && (
                <Tooltip title="Permanently Delete User" arrow>
                  <IconButton size="small" onClick={() => askPermanentDelete(item)}>
                    <MdDeleteForever className="text-red-600" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ],
    [confirmResendUser?.id, isSuperAdmin, loadingResend, navigate, showDeactivated]
  );

  const table = useMaterialReactTable({
    columns,
    data: visibleUsers || [],
    rowNumberDisplayMode: "original",
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      sorting: [{ id: "name", desc: false }],
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "70vh",
        overflowY: "auto",
      },
    },
    state: {
      isLoading: isVisibleLoading,
      showProgressBars: isVisibleLoading,
      isSaving: isVisibleLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (!eventClick(event)) {
          navigate(`/admin/user/view/${row?.original?.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between">
              <h3 className={"font-semibold text-lg " + "text-app-text"}>
                {showDeactivated ? "Deactivated users" : "Users"} ({visibleUsers?.length || 0}){" "}
                <RefreshData
                  refreshData={showDeactivated ? refetchDeactivatedUsers : refetchUsers}
                />
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={showDeactivated ? "text" : "contained"}
                  size="small"
                  onClick={() => setShowDeactivated(false)}
                >
                  Active
                </Button>
                <Button
                  variant={showDeactivated ? "contained" : "text"}
                  size="small"
                  onClick={() => setShowDeactivated(true)}
                >
                  Deactivated
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  to={isAdminOrSuperAdmin ? "/admin/user/add" : "/admin/user/add/referral"}
                >
                  Add User
                </Button>
              </div>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="User"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={deleteUser}
        />
      )}
      <ConfirmDialog
        open={confirmResendUser != null}
        title="Resend invitation email?"
        message={`A new temporary password will be generated and emailed to ${
          confirmResendUser?.email || "the user"
        }, replacing their current one. Continue?`}
        confirmLabel="Resend"
        loading={loadingResend}
        onConfirm={() => resendVerification(confirmResendUser?.id)}
        onCancel={() => setConfirmResendUser(null)}
      />
      <ConfirmDialog
        open={confirmReactivateUser != null}
        title="Reactivate this user?"
        message={`"${
          confirmReactivateUser?.name || confirmReactivateUser?.email || "The user"
        }" will be able to sign in again.`}
        confirmLabel="Reactivate"
        onConfirm={reactivateUser}
        onCancel={() => setConfirmReactivateUser(null)}
      />
      <ConfirmDialog
        open={permanentDeleteTarget != null}
        title="Permanently delete this user?"
        message={
          loadingPreview
            ? "Loading what will be removed…"
            : deletePreview
            ? [
                `Permanently delete the deactivated account "${
                  deletePreview.name || deletePreview.email
                }" (${deletePreview.email})?`,
                deletePreview.ownedRowsDeleted > 0 &&
                  `${deletePreview.ownedRowsDeleted} owned row(s) go with it.`,
                deletePreview.attributionsCleared > 0 &&
                  `${deletePreview.attributionsCleared} who-did-it reference(s) on kept records will be cleared.`,
                `The email address becomes available for a new account.`,
                ...(deletePreview.warnings || []).filter((warning) =>
                  /cannot be undone|Resolve the blockers/i.test(warning)
                ),
              ]
                .filter(Boolean)
                .join(" ")
            : "Permanently delete this deactivated account? This cannot be undone."
        }
        confirmLabel={deletePreview && !deletePreview.canDelete ? "Close" : "Delete permanently"}
        confirmColor={deletePreview && !deletePreview.canDelete ? "primary" : "error"}
        loading={loadingPreview}
        onConfirm={() => {
          if (deletePreview && !deletePreview.canDelete) {
            setPermanentDeleteTarget(null);
            setDeletePreview(null);
            return;
          }
          permanentlyDeleteUser();
        }}
        onCancel={() => {
          setPermanentDeleteTarget(null);
          setDeletePreview(null);
        }}
      />
    </div>
  );
};

export default Users;
